import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import {
  Plus,
  Calendar,
  Users,
  Video,
  Edit3,
  Power,
  Play,
  Square,
  Ban,
  Archive,
  Eye,
  Link2,
  Search,
  ChevronDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { getUserFacingError } from '../../services/errorMessage';
import { parseLanguageCsv } from '../../utils/languageOptions';

const STATUS_ORDER = { ONGOING: 0, SCHEDULED: 1, COMPLETED: 2, ARCHIVED: 3, CANCELLED: 4 };

const getLanguageList = (meeting) => parseLanguageCsv(meeting?.language);

const sortMeetings = (arr) => [...arr].sort((a, b) => {
  const orderDiff = (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5);
  if (orderDiff !== 0) return orderDiff;
  return new Date(a.startTime) - new Date(b.startTime);
});

const AdminMeetings = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const statusMenuRef = useRef(null);
  const [allMeetings, setAllMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState(['upcoming']);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveLink, setArchiveLink] = useState('');
  const [savingArchiveLink, setSavingArchiveLink] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancellingMeeting, setCancellingMeeting] = useState(false);
  const [stats, setStats] = useState({ total: 0, scheduled: 0, ongoing: 0, completed: 0 });

  const statusOptions = useMemo(() => ([
    { value: 'upcoming', label: 'Upcoming & Live' },
    { value: 'ongoing', label: 'Live Now' },
    { value: 'all', label: 'All' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'archived', label: 'Archived' },
    { value: 'disabled', label: 'Disabled' },
  ]), []);

  useEffect(() => {
    fetchMeetings();
  }, []);

  useEffect(() => {
    const scheduledCount = allMeetings.filter((m) => m.status === 'SCHEDULED').length;
    const ongoingCount = allMeetings.filter((m) => m.status === 'ONGOING').length;
    const completedCount = allMeetings.filter((m) => m.status === 'COMPLETED').length;

    setStats({
      total: allMeetings.length,
      scheduled: scheduledCount,
      ongoing: ongoingCount,
      completed: completedCount,
    });

    if (selectedStatusFilters.length === 1 && selectedStatusFilters[0] === 'upcoming' && scheduledCount + ongoingCount === 0) {
      setSelectedStatusFilters(['all']);
    }
  }, [allMeetings, selectedStatusFilters]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setShowStatusMenu(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const toggleStatusFilter = (value) => {
    setSelectedStatusFilters((current) => {
      if (value === 'all') {
        return ['all'];
      }

      const withoutAll = current.filter((item) => item !== 'all');
      const alreadySelected = withoutAll.includes(value);

      if (alreadySelected) {
        const next = withoutAll.filter((item) => item !== value);
        return next.length ? next : ['all'];
      }

      return [...withoutAll, value];
    });
  };

  const statusFilterLabel = useMemo(() => {
    if (selectedStatusFilters.includes('all')) {
      return 'All statuses';
    }

    if (selectedStatusFilters.length === 1) {
      const selected = statusOptions.find((option) => option.value === selectedStatusFilters[0]);
      return selected?.label || 'Status';
    }

    return `${selectedStatusFilters.length} statuses selected`;
  }, [selectedStatusFilters, statusOptions]);

  const fetchMeetings = async () => {
    try {
      setError('');
      setLoading(true);
      const response = await api.get('/meetings');
      setAllMeetings(response.data || []);
    } catch (err) {
      setError(getUserFacingError(err, 'Unable to load meetings right now.'));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinMeeting = async (meeting) => {
    try {
      setError('');
      setSuccessMessage('');
      const response = await api.post('/meetings/secure/generate-join-link', {
        meetingId: meeting.id,
      });

      if (response.data?.joinLink) {
        sessionStorage.setItem(`meeting_token_${meeting.id}`, response.data.token);
        navigate(`/meeting/join?meetingId=${meeting.id}&token=${response.data.token}`);
      }
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to generate meeting link'));
    }
  };

  const handleEndMeeting = async (meeting) => {
    try {
      setError('');
      setSuccessMessage('');
      await api.post(`/meetings/${meeting.id}/end`);
      fetchMeetings();
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to end meeting'));
    }
  };

  const handleDelete = async () => {
    if (!selectedMeeting) return;

    try {
      setError('');
      setSuccessMessage('');
      await api.patch(`/meetings/${selectedMeeting.id}/toggle`, {
        isActive: !selectedMeeting.isActive,
      });
      const wasActive = selectedMeeting.isActive;
      setShowDeleteModal(false);
      setSelectedMeeting(null);
      setSuccessMessage(
        wasActive
          ? 'Meeting hidden from users and speakers successfully.'
          : 'Meeting is visible to users and speakers again.'
      );
      fetchMeetings();
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to update meeting visibility'));
    }
  };

  const openCancelModal = (meeting) => {
    setSelectedMeeting(meeting);
    setCancelReason('');
    setCancelMessage('');
    setError('');
    setSuccessMessage('');
    setShowCancelModal(true);
  };

  const handleCancelMeeting = async () => {
    if (!selectedMeeting) return;

    try {
      setCancellingMeeting(true);
      setError('');
      setSuccessMessage('');
      const response = await api.patch(`/meetings/${selectedMeeting.id}/cancel`, {
        reason: cancelReason.trim(),
        message: cancelMessage.trim(),
      });
      setShowCancelModal(false);
      setSelectedMeeting(null);
      setCancelReason('');
      setCancelMessage('');
      setSuccessMessage(response.data?.message || 'Meeting cancelled successfully.');
      fetchMeetings();
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to cancel meeting'));
    } finally {
      setCancellingMeeting(false);
    }
  };

  const handlePlayArchive = (meeting) => {
    if (!meeting.archiveRecordingUrl) {
      return;
    }

    navigate(`/meeting/stream/${meeting.id}`, {
      state: {
        streamEmbedUrl: meeting.archiveRecordingUrl,
        meetingTitle: `${meeting.title} (Recording)`,
      },
    });
  };

  const openArchiveModal = (meeting) => {
    setSelectedMeeting(meeting);
    setArchiveLink(meeting.archiveRecordingUrl || '');
    setShowArchiveModal(true);
  };

  const handleSaveArchiveLink = async () => {
    if (!selectedMeeting || !archiveLink.trim()) {
      return;
    }

    try {
      setSavingArchiveLink(true);
      setError('');
      await api.patch(`/meetings/${selectedMeeting.id}/archive-link`, {
        archiveRecordingUrl: archiveLink.trim(),
      });
      setShowArchiveModal(false);
      setSelectedMeeting(null);
      setArchiveLink('');
      fetchMeetings();
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to save archive link'));
    } finally {
      setSavingArchiveLink(false);
    }
  };

  const getSpeakerNames = (meeting) =>
    (meeting.speakers || []).map((s) => s.speaker?.name || s.name).filter(Boolean).join(', ');

  const matchFilters = (meeting) => {
    const q = searchTerm.toLowerCase();
    const topic = (meeting.topic || meeting.title || '').toLowerCase();
    const languages = getLanguageList(meeting).map((item) => item.toLowerCase());
    const doctors = getSpeakerNames(meeting).toLowerCase();
    const dateLabel = format(new Date(meeting.startTime), 'yyyy-MM-dd');

    const matchesSearch =
      !q ||
      meeting.title.toLowerCase().includes(q) ||
      meeting.description?.toLowerCase().includes(q) ||
      topic.includes(q);

    const matchesDoctor = !doctorFilter || doctors.includes(doctorFilter.toLowerCase());
    const matchesTopic = !topicFilter || topic.includes(topicFilter.toLowerCase());
    const matchesLanguage = languageFilter === 'all' || languages.includes(languageFilter.toLowerCase());
    const matchesDate = !dateFilter || dateLabel === dateFilter;

    return matchesSearch && matchesDoctor && matchesTopic && matchesLanguage && matchesDate;
  };

  const availableLanguages = Array.from(
    new Set(
      allMeetings.flatMap((meeting) => getLanguageList(meeting))
    )
  ).sort((a, b) => a.localeCompare(b));

  const meetings = sortMeetings(
    allMeetings.filter((meeting) => {
      if (selectedStatusFilters.includes('all')) return true;

      return selectedStatusFilters.some((statusFilter) => {
        if (statusFilter === 'upcoming') return ['SCHEDULED', 'ONGOING'].includes(meeting.status);
        if (statusFilter === 'disabled') return !meeting.isActive;
        return meeting.status === statusFilter.toUpperCase();
      });
    })
    .filter((meeting) => matchFilters(meeting))
  );

  const getStatusBadge = (status, isActive) => {
    const statusMap = {
      SCHEDULED: { variant: 'primary', label: 'Scheduled' },
      ONGOING: { variant: 'success', label: 'Live Now' },
      COMPLETED: { variant: 'default', label: 'Completed' },
      ARCHIVED: { variant: 'warning', label: 'Archived' },
      CANCELLED: { variant: 'danger', label: 'Cancelled' },
    };

    const config = statusMap[status] || statusMap.SCHEDULED;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Badge variant={config.variant} size="sm" status>
          {config.label}
        </Badge>
        {!isActive && (
          <Badge variant="danger" size="sm" status>
            Disabled
          </Badge>
        )}
      </div>
    );
  };

  const columns = [
    {
      key: 'title',
      label: 'Meeting',
      sortable: true,
      render: (meeting) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {meeting.bannerUrl ? (
            <img
              src={meeting.bannerUrl}
              alt={meeting.title}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '8px',
                backgroundColor: theme.colors.backgroundSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Video size={24} color={theme.colors.textSecondary} />
            </div>
          )}
          <div>
            <div style={{ fontWeight: '600', color: theme.colors.text }}>{meeting.title}</div>
            <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>Room: {meeting.roomId.substring(0, 8)}...</div>
              {meeting.topic && <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Topic: {meeting.topic}</div>}
              {meeting.language && <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Languages: {getLanguageList(meeting).join(', ')}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'startTime',
      label: 'Date & Time',
      sortable: true,
      render: (meeting) => (
        <div>
          <div style={{ color: theme.colors.text }}>{format(new Date(meeting.startTime), 'MMM dd, yyyy')}</div>
          <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>{format(new Date(meeting.startTime), 'h:mm a')}</div>
        </div>
      ),
    },
    {
      key: 'speakers',
      label: 'Speakers',
      render: (meeting) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Users size={16} color={theme.colors.textSecondary} />
          <span>{meeting.speakers?.length || 0}</span>
          <span style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>
            {meeting.speakers?.length ? `(${getSpeakerNames(meeting)})` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (meeting) => getStatusBadge(meeting.status, meeting.isActive),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (meeting) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="sm"
            variant="ghost"
            icon={Eye}
            onClick={() => navigate(`/admin/meetings/${meeting.id}`)}
            title="View"
          />
          <Button
            size="sm"
            variant="ghost"
            icon={Edit3}
            onClick={() => navigate(`/admin/meetings/${meeting.id}/edit`)}
            title="Edit"
          />
          {meeting.status === 'SCHEDULED' && meeting.isActive && (
            <Button
              size="sm"
              variant="ghost"
              icon={Play}
              onClick={() => handleJoinMeeting(meeting)}
              title="Join Meeting"
            />
          )}
          {meeting.status === 'ONGOING' && (
            <>
              <Button
                size="sm"
                variant="ghost"
                icon={Play}
                onClick={() => handleJoinMeeting(meeting)}
                title="Join Meeting"
              />
              <Button
                size="sm"
                variant="ghost"
                icon={Square}
                onClick={() => handleEndMeeting(meeting)}
                title="End Meeting"
              />
            </>
          )}
          {['COMPLETED', 'ARCHIVED'].includes(meeting.status) && (
            <Button
              size="sm"
              variant="ghost"
              icon={Link2}
              onClick={() => openArchiveModal(meeting)}
              title="Set Recording Link"
            />
          )}
          {meeting.archiveRecordingUrl && (
            <Button
              size="sm"
              variant="ghost"
              icon={Play}
              onClick={() => handlePlayArchive(meeting)}
              title="Play Recording"
            />
          )}
          {['SCHEDULED', 'ONGOING'].includes(meeting.status) && (
            <Button
              size="sm"
              variant="ghost"
              icon={Ban}
              onClick={() => openCancelModal(meeting)}
              title="Cancel Meeting"
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={Power}
            onClick={() => {
              setSelectedMeeting(meeting);
              setShowDeleteModal(true);
            }}
            title={meeting.isActive ? 'Deactivate (Hide from Users/Speakers)' : 'Activate (Show to Users/Speakers)'}
          />
        </div>
      ),
    },
  ];

  const statCards = [
    { label: 'Total Meetings', value: stats.total, icon: Video, color: theme.colors.primary },
    { label: 'Scheduled', value: stats.scheduled, icon: Calendar, color: '#3b82f6' },
    { label: 'Ongoing', value: stats.ongoing, icon: Play, color: theme.colors.success },
    { label: 'Completed', value: stats.completed, icon: Archive, color: theme.colors.secondary },
  ];

  const styles = {
    container: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '8px',
    },
    error: {
      marginBottom: '12px',
      padding: '12px',
      borderRadius: '8px',
      backgroundColor: theme.colors.error + '18',
      border: `1px solid ${theme.colors.error}44`,
      color: theme.colors.error,
      fontSize: '14px',
    },
    success: {
      marginBottom: '12px',
      padding: '12px',
      borderRadius: '8px',
      backgroundColor: theme.colors.success + '18',
      border: `1px solid ${theme.colors.success}44`,
      color: theme.colors.success,
      fontSize: '14px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '14px',
      marginBottom: '24px',
    },
    statCard: (color) => ({
      padding: '16px',
      borderRadius: '12px',
      backgroundColor: theme.colors.cardBg,
      border: `1px solid ${theme.colors.border}`,
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }),
    statIcon: (color) => ({
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    statContent: {
      flex: 1,
    },
    statLabel: {
      fontSize: '13px',
      color: theme.colors.textSecondary,
      marginBottom: '4px',
      fontWeight: '500',
    },
    statValue: {
      fontSize: '22px',
      fontWeight: '700',
      color: theme.colors.text,
    },
    searchWrap: {
      position: 'relative',
      width: '100%',
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.colors.textTertiary,
      pointerEvents: 'none',
    },
    searchInput: {
      width: '100%',
      padding: '10px 14px 10px 40px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '10px',
      marginTop: '10px',
    },
    filterInput: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    textArea: {
      width: '100%',
      minHeight: '110px',
      padding: '10px 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    modalFieldLabel: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    controlsTopRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(300px, 1fr) auto auto',
      gap: '10px',
      alignItems: 'center',
    },
    statusDropdownWrap: {
      position: 'relative',
      zIndex: 50,
    },
    statusDropdownBtn: {
      height: '40px',
      minWidth: '210px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      padding: '0 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
      boxSizing: 'border-box',
    },
    statusDropdownMenu: {
      position: 'absolute',
      top: 'calc(100% + 8px)',
      right: 0,
      width: '260px',
      maxHeight: '260px',
      overflowY: 'auto',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      boxShadow: theme.shadows.lg,
      padding: '10px',
      zIndex: 100,
    },
    statusOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 6px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '13px',
      color: theme.colors.text,
    },
    filterBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginTop: '10px',
    },
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading meetings..." />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {error && <div style={styles.error}>{error}</div>}
        {successMessage && <div style={styles.success}>{successMessage}</div>}
      </div>

      <div style={styles.statsGrid}>
        {statCards.map((stat, index) => (
          <div
            key={index}
            style={styles.statCard(stat.color)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = theme.shadows.md;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.statIcon(stat.color)}>
              <stat.icon size={24} color={stat.color} />
            </div>
            <div style={styles.statContent}>
              <div style={styles.statLabel}>{stat.label}</div>
              <div style={styles.statValue}>{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      <Card compact cardStyle={{ overflow: 'visible' }} bodyStyle={{ overflow: 'visible' }}>
        <div style={styles.controlsTopRow}>
          <div style={styles.searchWrap}>
            <Search size={16} style={styles.searchIcon} />
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={styles.statusDropdownWrap} ref={statusMenuRef}>
            <button
              type="button"
              style={styles.statusDropdownBtn}
              onClick={() => setShowStatusMenu((prev) => !prev)}
            >
              <span>{statusFilterLabel}</span>
              <ChevronDown size={16} />
            </button>

            {showStatusMenu && (
              <div style={styles.statusDropdownMenu}>
                {statusOptions.map((option) => {
                  const checked = selectedStatusFilters.includes(option.value);
                  return (
                    <label key={option.value} style={styles.statusOption}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatusFilter(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <Button icon={Plus} onClick={() => navigate('/admin/meetings/create')}>
            Create Meeting
          </Button>
        </div>

        <div style={styles.filtersGrid}>
          <input
            style={styles.filterInput}
            type="text"
            placeholder="Doctor"
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
          />
          <input
            style={styles.filterInput}
            type="text"
            placeholder="Topic"
            value={topicFilter}
            onChange={(e) => setTopicFilter(e.target.value)}
          />
          <input
            style={styles.filterInput}
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
          <select
            style={styles.filterInput}
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <option value="all">All Languages</option>
            {availableLanguages.map((language) => (
              <option key={language} value={language.toLowerCase()}>{language}</option>
            ))}
          </select>
        </div>
      </Card>

      <div style={{ marginTop: '24px' }}>
        {meetings.length === 0 ? (
          <EmptyState
            icon={Video}
            title="No meetings found"
            description={
              selectedStatusFilters.includes('all')
                ? 'Create your first meeting to get started with hosting conferences.'
                : 'No meetings match the selected status filters. Try changing the filters.'
            }
            action={() => navigate('/admin/meetings/create')}
            actionLabel="Create Meeting"
          />
        ) : (
          <Table columns={columns} data={meetings} loading={loading} emptyMessage="No meetings found" />
        )}
      </div>

      <Modal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        title="Recording Link"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveArchiveLink} loading={savingArchiveLink}>
              Save Link
            </Button>
          </>
        }
      >
        <p style={{ color: theme.colors.textSecondary, marginTop: 0 }}>
          Add the YouTube recording URL for {selectedMeeting?.title}.
        </p>
        <input
          type="url"
          value={archiveLink}
          onChange={(e) => setArchiveLink(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          style={styles.searchInput}
        />
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={selectedMeeting?.isActive ? 'Deactivate Meeting' : 'Activate Meeting'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant={selectedMeeting?.isActive ? 'danger' : 'primary'} onClick={handleDelete}>
              {selectedMeeting?.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        }
      >
        <p style={{ color: theme.colors.text, lineHeight: '1.6' }}>
          {selectedMeeting?.isActive
            ? `Deactivate "${selectedMeeting?.title}"? It will be hidden from users and speakers but still visible to admins.`
            : `Activate "${selectedMeeting?.title}"? It will become visible to users and speakers again.`}
        </p>
      </Modal>

      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Meeting"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              Close
            </Button>
            <Button variant="danger" onClick={handleCancelMeeting} loading={cancellingMeeting}>
              Confirm Cancellation
            </Button>
          </>
        }
      >
        <p style={{ color: theme.colors.text, lineHeight: '1.6', marginTop: 0 }}>
          Cancel "{selectedMeeting?.title}" and email all active users and speakers.
        </p>
        <div style={{ marginBottom: '14px' }}>
          <label style={styles.modalFieldLabel}>Reason</label>
          <input
            type="text"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Optional short reason"
            style={styles.filterInput}
          />
        </div>
        <div>
          <label style={styles.modalFieldLabel}>Email Message</label>
          <textarea
            value={cancelMessage}
            onChange={(e) => setCancelMessage(e.target.value)}
            placeholder="Write the message that will be sent in the cancellation email."
            style={styles.textArea}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AdminMeetings;
